import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Save,
  X,
  Type,
  Hash,
  Calendar,
  ListChecks,
  HelpCircle,
  Table as TableIcon,
  Edit,
  Calculator,
  Settings
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  generateAttributeFieldName,
  formatNumber
} from './utils';
import { evaluateFormula, calculateTotals } from './formulaEvaluator';
import CustomAttributesConfig from './CustomAttributesConfig';
import AutocompleteInput from './AutocompleteInput';
import { useFieldSuggestions } from './useFieldSuggestions';

const ATTRIBUTE_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'choice', label: 'Choice' },
  { value: 'table', label: 'Table' }
];

/**
 * Helper component for Table attributes
 */
const TableAttributeInput = ({ field, value = [], onChange, entityName }) => {
  const rawColumns = field.columns || field.ui?.columns || [];
  const columns = Array.isArray(rawColumns) ? rawColumns : [];
  const rows = Array.isArray(value) ? value : [];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [modalRowData, setModalRowData] = useState({});

  // Calculate totals for columns with showTotal flag
  const totals = useMemo(() => {
    return calculateTotals(rows, columns, evaluateFormula);
  }, [rows, columns]);

  const handleCellChange = (rowIndex, colKey, cellValue) => {
    const newRows = [...rows];
    if (!newRows[rowIndex]) newRows[rowIndex] = {};
    newRows[rowIndex] = { ...newRows[rowIndex], [colKey]: cellValue };
    onChange(newRows);
  };

  const addRow = () => {
    onChange([...rows, {}]);
  };

  const removeRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    onChange(newRows);
  };

  const openModalForNewRow = () => {
    setEditingRowIndex(null);
    setModalRowData({});
    setIsModalOpen(true);
  };

  const openModalForEdit = (rowIndex) => {
    setEditingRowIndex(rowIndex);
    setModalRowData({ ...rows[rowIndex] });
    setIsModalOpen(true);
  };

  const handleModalFieldChange = (colKey, value) => {
    setModalRowData((prev) => ({ ...prev, [colKey]: value }));
  };

  const handleModalSave = () => {
    if (editingRowIndex !== null) {
      // Update existing row
      const newRows = [...rows];
      newRows[editingRowIndex] = modalRowData;
      onChange(newRows);
    } else {
      // Add new row
      onChange([...rows, modalRowData]);
    }
    setIsModalOpen(false);
    setModalRowData({});
    setEditingRowIndex(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setModalRowData({});
    setEditingRowIndex(null);
  };

  if (columns.length === 0) {
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          No columns defined for this table. Please contact administrator to configure table columns.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {columns.map((col, idx) => (
              <th
                key={col.key || idx}
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                <div className="flex items-center space-x-1">
                  <span>{typeof col.label === 'string' ? col.label : String(col.label || '')}</span>
                  {col.type === 'calculated' && (
                    <Calculator className="h-3 w-3 text-blue-500" title="Calculated Column" />
                  )}
                </div>
              </th>
            ))}
            <th className="px-3 py-2 w-10"></th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((col) => {
                // Calculate value for calculated columns
                let displayValue;
                if (col.type === 'calculated' && col.formula) {
                  const calculated = evaluateFormula(col.formula, row, columns);
                  if (calculated !== null && !isNaN(calculated)) {
                    const formatConfig = col.numberFormat || { preset: 'decimal2' };
                    displayValue = formatNumber(calculated, formatConfig);
                  } else {
                    displayValue = 'Error';
                  }
                } else if (col.type === 'number') {
                  const value = row[col.key];
                  if (value !== undefined && value !== null && value !== '') {
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    if (!isNaN(numValue)) {
                      const formatConfig = col.numberFormat || { preset: 'none' };
                      displayValue = formatNumber(numValue, formatConfig);
                    } else {
                      displayValue = String(value);
                    }
                  } else {
                    displayValue = '-';
                  }
                } else {
                  displayValue = typeof row[col.key] === 'object'
                    ? JSON.stringify(row[col.key])
                    : row[col.key] || '-';
                }

                return (
                  <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                    <div className={`text-sm truncate max-w-[150px] ${
                      col.type === 'calculated'
                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {displayValue}
                    </div>
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end space-x-1">
                  <button
                    onClick={() => openModalForEdit(rowIndex)}
                    className="text-gray-400 hover:text-blue-500 transition-colors"
                    type="button"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeRow(rowIndex)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    type="button"
                    title="Delete"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + 1}
                className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                No data
              </td>
            </tr>
          )}
        </tbody>
        {/* Total Row */}
        {rows.length > 0 && Object.keys(totals).length > 0 && (
          <tfoot className="bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600">
            <tr>
              {columns.map((col) => (
                <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                  {col.showTotal && totals[col.key] !== undefined ? (
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {typeof totals[col.key] === 'number'
                        ? formatNumber(totals[col.key], col.numberFormat || { preset: 'decimal2' })
                        : totals[col.key]}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {columns.indexOf(col) === 0 ? 'Total:' : ''}
                    </div>
                  )}
                </td>
              ))}
              <td className="px-3 py-2"></td>
            </tr>
          </tfoot>
        )}
      </table>
      <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={openModalForNewRow}
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center font-medium"
          type="button"
        >
          <Plus className="h-3 w-3 mr-1" /> Add Row
        </button>
      </div>

      {/* Modal for adding/editing row */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingRowIndex !== null ? 'Edit Row' : 'Add Row'}
              </h4>
              <button
                onClick={handleModalCancel}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {columns.filter(col => col.type !== 'calculated').map((col) => {
                const colType = col.type || 'text';
                const colValue = typeof modalRowData[col.key] === 'object'
                  ? JSON.stringify(modalRowData[col.key])
                  : modalRowData[col.key] || '';
                
                // Use AutocompleteInput for text columns if entityName is available
                const useAutocomplete = colType === 'text' && entityName && field?.name;
                
                return (
                  <div key={col.key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {typeof col.label === 'string' ? col.label : String(col.label || '')}
                    </label>
                    {useAutocomplete ? (
                      <TableColumnAutocompleteInput
                        field={field}
                        columnKey={col.key}
                        value={String(colValue)}
                        onChange={(newValue) => handleModalFieldChange(col.key, newValue)}
                        entityName={entityName}
                      />
                    ) : (
                      <input
                        type={colType}
                        value={colValue}
                        onChange={(e) => handleModalFieldChange(col.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                      />
                    )}
                  </div>
                );
              })}
              {/* Show calculated columns preview */}
              {columns.filter(col => col.type === 'calculated').length > 0 && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Calculated Fields (read-only):</p>
                  {columns.filter(col => col.type === 'calculated').map((col) => {
                    const calculated = col.formula ? evaluateFormula(col.formula, modalRowData, columns) : null;
                    return (
                      <div key={col.key} className="flex justify-between items-center text-sm mb-1">
                        <span className="text-gray-700 dark:text-gray-300">{col.label}:</span>
                        <span className="font-medium text-blue-600 dark:text-blue-400">
                          {calculated !== null ? calculated.toFixed(2) : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
              <button
                type="button"
                onClick={handleModalCancel}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalSave}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm"
              >
                <Save className="h-4 w-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper component that wraps AutocompleteInput with useFieldSuggestions hook
 */
const FieldAutocompleteInput = ({ field, value, onChange, entityName }) => {
  const { suggestions, loading } = useFieldSuggestions(
    entityName,
    field.name,
    null, // columnKey is null for regular fields
    true // enabled
  );

  return (
    <AutocompleteInput
      value={value}
      onChange={onChange}
      suggestions={suggestions}
      loading={loading}
      placeholder=""
      type="text"
    />
  );
};

/**
 * Helper component for table column autocomplete inputs
 */
const TableColumnAutocompleteInput = ({ field, columnKey, value, onChange, entityName }) => {
  const { suggestions, loading } = useFieldSuggestions(
    entityName,
    field.name,
    columnKey, // columnKey for table columns
    true // enabled
  );

  return (
    <AutocompleteInput
      value={value}
      onChange={onChange}
      suggestions={suggestions}
      loading={loading}
      placeholder=""
      type="text"
    />
  );
};

/**
 * Shared editor for entity custom attributes.
 * Handles value entry and creation of new custom attribute fields.
 */
const CustomAttributesEditor = ({
  fields = [],
  attributes = {},
  onChange,
  onAddField,
  title,
  allExistingFieldNames = [], // All field names from both global and tenant configs
  entityName, // Entity name for configuration
  onFieldsUpdated // Callback when fields are updated via config modal
}) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useAuth();
  const isThai = i18n?.language?.toLowerCase().startsWith('th') || false;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldNameTH, setNewFieldNameTH] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [isSavingField, setIsSavingField] = useState(false);
  const [choiceOptionsInput, setChoiceOptionsInput] = useState('');
  const [tableColumnsInput, setTableColumnsInput] = useState('');

  // Check if user can configure - allow Admin or Manager roles
  const hasAdminRole = hasRole('Admin');
  const hasManagerRole = hasRole('Manager');
  const hasAnyRole = hasRole(['Manager', 'Admin']);
  const canConfigure = (hasAdminRole || hasManagerRole || hasAnyRole) && entityName;

  // Combine custom attribute field names with all existing field names
  const existingFieldNames = useMemo(() => {
    const customFieldNames = fields.map((field) => field.name);
    const allNames = new Set([...customFieldNames, ...allExistingFieldNames]);
    return Array.from(allNames);
  }, [fields, allExistingFieldNames]);

  const getFieldLabel = (field) => {
    if (!field) return '';
    let label;
    if (isThai) {
      label = field.displayNameTH || field.displayName || field.name;
    } else {
      label = field.displayName || field.displayNameTH || field.name;
    }
    return typeof label === 'string' ? label : String(label || '');
  };

  const getOptionLabel = (option) => {
    if (!option) return '';

    if (typeof option === 'string') {
      return option;
    }

    let label;
    if (isThai) {
      label = option.labelTH || option.label || option.value || '';
    } else {
      label = option.label || option.labelTH || option.value || '';
    }
    return typeof label === 'string' ? label : String(label || '');
  };

  const normaliseOptionValue = (value = '', fallbackIndex = 0) => {
    const stringValue = value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_|_$/g, '');

    if (stringValue) return stringValue;

    return `option_${fallbackIndex + 1}`;
  };

  const parseChoiceOptionsInput = (input) => {
    const lines = input
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const options = [];

    lines.forEach((line, index) => {
      const parts = line.split('|').map((part) => part.trim());
      if (!parts.length) return;

      const label = parts[0];
      if (!label) return;

      const explicitValue = parts[1] || '';
      const labelTH = parts[2] || '';

      let value = normaliseOptionValue(explicitValue || label, index);

      let uniqueValue = value;
      let counter = 1;
      while (options.some((option) => option.value === uniqueValue)) {
        counter += 1;
        uniqueValue = normaliseOptionValue(`${value}_${counter}`, index + counter);
      }

      const option = {
        value: uniqueValue,
        label: label
      };

      if (labelTH) {
        option.labelTH = labelTH;
      }

      options.push(option);
    });

    return options;
  };

  const parseTableColumnsInput = (input) => {
    const lines = input
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    return lines.map((line, index) => {
      const parts = line.split('|').map((part) => part.trim());
      const label = parts[0];
      const typeRaw = (parts[1] || 'text').toLowerCase();
      const keyRaw = parts[2] || label;

      const validTypes = ['text', 'number', 'date'];
      const type = validTypes.includes(typeRaw) ? typeRaw : 'text';
      
      // Generate a safe key
      let key = normaliseOptionValue(keyRaw, index);
      
      return {
        label,
        type,
        key
      };
    }).filter(col => col.label);
  };

  const handleValueChange = (field, rawValue) => {
    if (!onChange) return;

    const attributeType =
      field?.metadata?.attributeType ||
      field?.ui?.inputType ||
      field?.dataType ||
      'text';

    let value = rawValue;

    if (attributeType === 'number') {
      value = rawValue === '' ? '' : Number(rawValue);
      if (rawValue !== '' && Number.isNaN(value)) {
        value = '';
      }
    }

    if (attributeType === 'date') {
      value = rawValue;
    }

    onChange({
      ...attributes,
      [field.name]: value
    });
  };

  const handleAddField = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🎯 CustomAttributesEditor: handleAddField called');
    console.log('   onAddField function exists:', !!onAddField);
    console.log('   newFieldName:', newFieldName);
    console.log('   newFieldType:', newFieldType);
    
    if (!onAddField) {
      console.error('❌ onAddField prop is not provided!');
      toast.error('Configuration error: onAddField handler missing');
      return;
    }

    const trimmedName = newFieldName.trim();
    if (!trimmedName) {
      console.warn('⚠️ Field label is empty');
      toast.error('Field label is required');
      return;
    }

    const fieldType = newFieldType || 'text';

    let choiceOptions = [];
    if (fieldType === 'choice') {
      choiceOptions = parseChoiceOptionsInput(choiceOptionsInput);
      if (choiceOptions.length === 0) {
        console.warn('⚠️ No choice options provided');
        toast.error(
          t(
            'customAttributes.choiceOptionsRequired',
            'Please provide at least one option (Label | value | Thai label optional).'
          )
        );
        return;
      }
    }

    let tableColumns = [];
    if (fieldType === 'table') {
      tableColumns = parseTableColumnsInput(tableColumnsInput);
      if (tableColumns.length === 0) {
        console.warn('⚠️ No table columns provided');
        toast.error(
          t(
            'customAttributes.tableColumnsRequired',
            'Please provide at least one column (Label | Type).'
          )
        );
        return;
      }
    }

    const generatedName = generateAttributeFieldName(
      trimmedName,
      existingFieldNames
    );
    
    console.log('   Generated field name:', generatedName);
    console.log('   Existing field names:', existingFieldNames);

    // Check if the generated field name already exists
    if (existingFieldNames.includes(generatedName)) {
      const errorMessage = `A field with the name "${generatedName}" already exists. Please use a different label.`;
      console.error(`❌ Duplicate field name detected: ${generatedName}`);
      toast.error(errorMessage);
      setIsSavingField(false);
      return;
    }

    setIsSavingField(true);
    try {
      const trimmedNameTH = newFieldNameTH.trim();
      console.log('   Calling onAddField with:', { fieldName: generatedName, label: trimmedName, labelTH: trimmedNameTH, type: fieldType });
      await onAddField({
        fieldName: generatedName,
        label: trimmedName,
        labelTH: trimmedNameTH || undefined,
        type: fieldType,
        options: choiceOptions,
        columns: tableColumns
      });
      console.log('✅ onAddField completed successfully');
      toast.success('Custom field added');
      setIsModalOpen(false);
      setNewFieldName('');
      setNewFieldNameTH('');
      setNewFieldType('text');
      setChoiceOptionsInput('');
      setTableColumnsInput('');
    } catch (err) {
      console.error('❌ Error in onAddField:', err);
      console.error('   Error message:', err?.message);
      console.error('   Error stack:', err?.stack);
      const message =
        err?.message || 'Failed to add custom field. Please try again.';
      toast.error(message);
    } finally {
      setIsSavingField(false);
      console.log('   isSavingField set to false');
    }
  };

  const renderInput = (field) => {
    const attributeType =
      field?.metadata?.attributeType ||
      field?.ui?.inputType ||
      field?.dataType ||
      'text';
    const value = attributes[field.name];

    const effectiveType = ['select', 'dropdown'].includes(attributeType)
      ? 'choice'
      : attributeType;

    switch (effectiveType) {
      case 'number':
        return (
          <input
            type="number"
            value={value === undefined || value === null ? '' : value}
            onChange={(event) => handleValueChange(field, event.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value ? value.slice(0, 10) : ''}
            onChange={(event) => handleValueChange(field, event.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
          />
        );
      case 'choice': {
        const rawOptions =
          (Array.isArray(field?.ui?.options) && field.ui.options) ||
          (Array.isArray(field?.validation?.enum) &&
            field.validation.enum.map((option) =>
              typeof option === 'string' ? { value: option, label: option } : option
            )) ||
          [];

        const options = rawOptions.map((option, index) => {
          if (typeof option === 'string') {
            return { value: option, label: option };
          }

          return {
            value: option.value ?? normaliseOptionValue(option.label, index),
            label: option.label ?? option.value ?? '',
            labelTH: option.labelTH
          };
        });

        return (
          <select
            value={value === undefined || value === null ? '' : value}
            onChange={(event) => handleValueChange(field, event.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
          >
            <option value="">
              {String(t('customAttributes.selectOption', 'Select an option'))}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {getOptionLabel(option)}
              </option>
            ))}
          </select>
        );
      }
      case 'table':
        return (
          <TableAttributeInput
            field={field}
            value={value}
            onChange={(newValue) => handleValueChange(field, newValue)}
            entityName={entityName}
          />
        );
      default:
        // Use AutocompleteInput for text fields if entityName is available
        if (entityName) {
          return (
            <FieldAutocompleteInput
              field={field}
              value={value === undefined || value === null ? '' : String(value)}
              onChange={(newValue) => handleValueChange(field, newValue)}
              entityName={entityName}
            />
          );
        }
        // Fallback to regular input if no entityName
        return (
          <input
            type="text"
            value={value === undefined || value === null ? '' : value}
            onChange={(event) => handleValueChange(field, event.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
          />
        );
    }
  };

  const renderTypeIcon = (field) => {
    const attributeTypeRaw =
      field?.metadata?.attributeType ||
      field?.ui?.inputType ||
      field?.dataType ||
      'text';

    const effectiveTypeStr = String(attributeTypeRaw || 'text');
    const effectiveType = ['select', 'dropdown'].includes(effectiveTypeStr)
      ? 'choice'
      : effectiveTypeStr;

    const iconClass =
      'h-4 w-4 text-gray-400 dark:text-gray-500';

    switch (effectiveType) {
      case 'text':
      case 'textarea':
        return <Type className={iconClass} title={t('customAttributes.types.text', 'Text')} />;
      case 'number':
        return <Hash className={iconClass} title={t('customAttributes.types.number', 'Number')} />;
      case 'date':
      case 'datetime':
        return <Calendar className={iconClass} title={t('customAttributes.types.date', 'Date')} />;
      case 'choice':
        return <ListChecks className={iconClass} title={t('customAttributes.types.choice', 'Choice')} />;
      case 'table':
        return <TableIcon className={iconClass} title={t('customAttributes.types.table', 'Table')} />;
      default:
        return <HelpCircle className={iconClass} title={effectiveType} />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {title || String(t('customers.additionalDataSection') || 'Additional Data')}
        </h3>
        <div className="flex items-center space-x-2">
          {canConfigure && (
            <button
              type="button"
              onClick={() => setShowConfig(true)}
              className="p-2 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
              title="Configure custom attributes"
            >
              <Settings className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center space-x-2 px-3 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>{String(t('customAttributes.addField', 'Add Field'))}</span>
          </button>
        </div>
      </div>

      {fields.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
          No additional fields configured yet. Click &ldquo;Add Field&rdquo; to
          create custom attributes for this entity.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field) => {
            const attributeTypeRaw =
              field?.metadata?.attributeType ||
              field?.ui?.inputType ||
              field?.dataType ||
              'text';

            const effectiveTypeStr = String(attributeTypeRaw || 'text');
            const effectiveType = ['select', 'dropdown'].includes(effectiveTypeStr)
              ? 'choice'
              : effectiveTypeStr;

            const isFullWidth = effectiveType === 'table' || effectiveType === 'textarea';

            return (
              <div
                key={field.name}
                className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2 ${
                  isFullWidth ? 'md:col-span-2' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {getFieldLabel(field)}
                  </label>
                  {field.metadata?.attributeType && (
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 dark:bg-gray-700">
                      {renderTypeIcon(field)}
                    </span>
                  )}
                </div>
                {renderInput(field)}
              </div>
            );
          })}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add Custom Field
              </h4>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setNewFieldName('');
                  setNewFieldNameTH('');
                  setChoiceOptionsInput('');
                  setTableColumnsInput('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Label (English)
                </label>
                <input
                  type="text"
                  value={newFieldName}
                  onChange={(event) => setNewFieldName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !isSavingField) {
                      event.preventDefault();
                      handleAddField(event);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                  placeholder="e.g., Contract Start Date"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Label (Thai) <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={newFieldNameTH}
                  onChange={(event) => setNewFieldNameTH(event.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                  placeholder="e.g., วันเริ่มสัญญา"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Field Type
                </label>
                <select
                  value={newFieldType}
                  onChange={(event) => {
                    const nextType = event.target.value;
                    setNewFieldType(nextType);
                    if (nextType !== 'choice') {
                      setChoiceOptionsInput('');
                    }
                    if (nextType !== 'table') {
                      setTableColumnsInput('');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                >
                  {ATTRIBUTE_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {String(t(`customAttributes.types.${option.value}`, option.label))}
                    </option>
                  ))}
                </select>
              </div>
              {newFieldType === 'choice' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {String(t('customAttributes.choiceOptions', 'Choice Options'))}
                  </label>
                  <textarea
                    value={choiceOptionsInput}
                    onChange={(event) => setChoiceOptionsInput(event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    rows={4}
                    placeholder={String(t(
                      'customAttributes.choiceOptionsPlaceholder',
                      'Enter one option per line. Format: Label | value | Thai label (optional)'
                    ))}
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line">
                    {String(t(
                      'customAttributes.choiceOptionsHelp',
                      'Example:\nStandard | standard\nPremium | premium\nVIP | vip | วีไอพี'
                    ))}
                  </p>
                </div>
              )}
              {newFieldType === 'table' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {String(t('customAttributes.tableColumns', 'Table Columns'))}
                  </label>
                  <textarea
                    value={tableColumnsInput}
                    onChange={(event) => setTableColumnsInput(event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                    rows={4}
                    placeholder={String(t(
                      'customAttributes.tableColumnsPlaceholder',
                      'Enter one column per line. Format: Label | Type (text, number, date)'
                    ))}
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line">
                    {String(t(
                      'customAttributes.tableColumnsHelp',
                      'Example:\nItem Name | text\nQuantity | number\nDue Date | date'
                    ))}
                  </p>
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setNewFieldName('');
                    setNewFieldNameTH('');
                    setChoiceOptionsInput('');
                    setTableColumnsInput('');
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={isSavingField}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddField}
                  disabled={isSavingField}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-60 transition-colors text-sm"
                >
                  {isSavingField ? (
                    <span className="flex items-center space-x-2">
                      <span className="h-4 w-4 border-b-2 border-white rounded-full animate-spin" />
                      <span>Saving</span>
                    </span>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save Field</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfig && canConfigure && (
        <CustomAttributesConfig
          fields={fields}
          entityName={entityName}
          onFieldsUpdated={async () => {
            if (onFieldsUpdated) {
              await onFieldsUpdated();
            }
            setShowConfig(false);
          }}
          onClose={() => setShowConfig(false)}
        />
      )}
    </div>
  );
};

export default CustomAttributesEditor;

